import Head from "next/head"
import { Fragment, useState } from 'react'
import { CheckIcon, MinusIcon, QuestionMarkCircleIcon } from '@heroicons/react/solid'
import { BadgeCheckIcon, CalendarIcon, TruckIcon } from '@heroicons/react/outline'
import Styles from './extras/styles/pricing.module.scss'
import { useRouter } from "next/router"
import Layout, { WidthConstraint } from "../components/3_organism/layout/layout"
import Button from "../components/1_atom/buttons/button"
import { capitalize } from "functions"
import { iPricingTableSection, iTeamBoostTier, prepiPricingTableFeatures, prepiTeamBoostTiers } from "data"
import Link from "next/link"
import dynamic from "next/dynamic";
import { prepiFeatures } from "typings"
import BuyBoosts from "../components/2_molecule/pricing/buyBoosts"

const Tooltip = dynamic(
  () => {
    return import("../components/1_atom/tooltip/tooltip");
  },
  { ssr: false }
);

export default function Pricing() {

  return (
    <Layout maxLayoutWidth="max">
      <Head>
        <title>Prepi pricing</title>
        <link rel="canonical" href={"https://prepi.io" + useRouter().pathname} />
      </Head>
      <PricingPage />
    </Layout>
  );
}

const faqs = [
  {
    id: 1,
    question: "Is there a money-back guarantee?",
    answer: "Yes, we offer a 30-day money-back guarantee if you are not satisfied with the product."
  },
  {
    id: 2,
    question: "Can I cancel my subscription at any time?",
    answer: "Yes, you can cancel your subscription at any time with no penalty."
  },
  {
    id: 3,
    question: "Are there any discounts available for long-term subscriptions?",
    answer: "Yes, we offer discounts for customers who commit to annual subscriptions."
  },
  {
    id: 4,
    question: "Is there a free trial period?",
    answer: "Yes, we offer a 14-day free trial for all plans so you can test out the product before committing."
  },
  {
    id: 5,
    question: "Can I upgrade or downgrade my plan at any time?",
    answer: "Yes, you can upgrade or downgrade your plan at any time to match your needs and budget."
  },
  {
    id: 6,
    question: "Are there any additional fees or hidden costs?",
    answer: "No, there are no additional fees or hidden costs. The prices listed on the pricing page are all-inclusive."
  }
]

const PricingPage = ({}) => {

  const FAQ = () => {
    return (
      <div className="">
        {/* FAQ */}
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <h2 className="text-center">
            Frequently asked questions
          </h2>
          <div className="mt-12">
            <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:grid-rows-2 md:gap-x-8 md:gap-y-12 lg:grid-cols-3">
              {faqs.map((faq) => (
                <div key={faq.id} className="space-y-2">
                  <dt className="text-lg leading-6 font-medium">{faq.question}</dt>
                  <dd className="text-base weak-text">{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    );
  };

  const TripleColumnIcon = () => {
    const incentives = [
      { name: 'Free trial', icon: TruckIcon },
      { name: 'Not happy? money back!', icon: BadgeCheckIcon },
      { name: 'Cancel anytime', icon: CalendarIcon },
    ]
  
    return (
      <div className="">
        <h2 className="sr-only">Why you should buy from us</h2>
        <div className="flex overflow-x-auto">
          <div className="mx-auto flex-row wrap gap5 justify-center whitespace-nowrap py-3 px-4 sm:px-6 lg:px-8">
            {incentives.map((incentive) => (
              <div key={incentive.name} className="flex-row text-sm font-medium text-primary">
                <incentive.icon className="mr-2 flex-none w-6 h-6" aria-hidden="true" />
                <p>{incentive.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w100 flex-col align-center">
      <BuyBoosts />
      <WidthConstraint maxLayoutWidth="medium">
        <div className="flex-col align-center">
          <h2>Team boost levels</h2>
          <h5 className="mt-5 text-center weak-text">
            {/* Boost your team to new heights with even more powerful features! */}
            Everything you need to get started is free, but there's more when you need it.
            {/* Getting started is free. Boost your team to unlock additional features! */}
          </h5>
        </div>
        <PricingTable sections={prepiPricingTableFeatures} />
      </WidthConstraint>

      <TripleColumnIcon />
      <FAQ />
    </div>
  )
}

const PricingTable = ({ sections }: { sections: iPricingTableSection[]}) => {
  const boostTiers = Object.values(prepiTeamBoostTiers).filter(tier => tier?.showInPricingPage === true)

  const TierPrice = ({ tier }: { tier: iTeamBoostTier }) => {
    if(tier.boostsRequired === 0) return <h3>Free forever</h3>

    return (
      <>
        <h3>
          {tier.boostsRequired}
          <span className="text-base font-medium weak-text"> boosts</span>
        </h3>
      </>
    )
  }
  return (
    <div className="mt-8">
      <table className="w-full h-px">
        <caption className="sr-only">Pricing plan comparison</caption>
        <thead>
          <tr>
            <th
              className="pb-4 pl-6 pr-6 text-sm font-medium text-left"
              scope="col"
              style={{ width: "20%" }}
            >
              <span className="sr-only">Feature by</span>
              <span>Boost tier</span>
            </th>
            {boostTiers.map((tier) => (
              <th
                key={tier.label}
                className="pb-4 px-6 text-lg leading-6 font-medium text-left"
                scope="col"
                style={{ width: "20%" }}
              >
                {capitalize(tier.label)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="">
          <tr>
            <th className="py-8 pl-6 pr-6 align-top text-sm font-medium text-left" scope="row">
              Boosts required
            </th>
            {boostTiers.map((tier) => (
              <td key={tier.label} className="h-full lg:py-8 lg:px-6 py-4 px-3 align-top">
                <div className="h-full flex flex-col justify-between">
                  <div>
                    <TierPrice tier={tier} />
                    <p className="mt-4 mb-4 text-sm">{tier.description}</p>
                  </div>
                  {tier.boostsRequired > 0 && (
                    <Link href={"/settings?show=premium"} prefetch={false}>
                      <Button
                        text={`Boost team to ${capitalize(tier.label)}`}
                        iconRight={{ icon: "link", fill: true }}
                        size="mini"
                        fullWidth
                        addClass={Styles.plusBtn}
                      />
                    </Link>
                  )}
                </div>
              </td>
            ))}
          </tr>
          {sections.map((section) => (
            <Fragment key={section.name}>
              <tr>
                <th
                  className="py-5 pl-6 bg2 rounded text-sm font-bold text-left"
                  colSpan={4}
                  scope="colgroup"
                >
                  {section.name}
                </th>
              </tr>
              {section.features.map((featureID) => {
                const feature = prepiFeatures?.[featureID]
                if (!feature) return <>{featureID} missing</>
                return (
                  <tr key={feature.labelShort}>
                    <th
                      className="py-5 pl-6 pr-6 text-sm font-normal text-left flex-row gap0 align-center"
                      scope="row"
                    >
                      {feature.labelShort}
                      {!!feature?.description && (
                        <Tooltip
                          style={{ maxWidth: 330 }}
                          content={<span>{feature?.description}</span>}
                        >
                          <QuestionMarkCircleIcon height={14} width={14} color="var(--inactive)" />
                        </Tooltip>
                      )}
                    </th>
                    {boostTiers.map((tier) => {
                      const tierHasFeature = tier.features?.[featureID]
                      return (
                        <td key={tier.label} className="py-5 px-6">
                          {typeof tierHasFeature === "number" ? (
                            <span className="block text-sm">
                              {tierHasFeature}
                              {!!feature?.pricingTableUnit
                                ? ` ${feature?.pricingTableUnit}${tierHasFeature > 1 ? "s" : ""}`
                                : ""}
                              {!!feature?.suffix && " " + feature.suffix}
                            </span>
                          ) : (
                            <>
                              {tierHasFeature === true ? (
                                <CheckIcon className="h-5 w-5 text-primary" aria-hidden="true" />
                              ) : (
                                <MinusIcon className="h-5 w-5 text-inactive" aria-hidden="true" />
                              )}

                              <span className="sr-only">
                                {tierHasFeature === true ? "Included" : "Not included"} in{" "}
                                {capitalize(tier.label)}
                              </span>
                            </>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr className="">
            <th className="sr-only" scope="row">
              Choose your boost tier
            </th>
            {boostTiers.map((tier) => (
              <td key={tier.label} className="pt-5 px-6">
                {typeof tier.boostsRequired === "number" && tier.boostsRequired > 0 && (
                  <Link href={"/settings?show=premium"} prefetch={false}>
                    <Button
                      text={`Boost team to ${capitalize(tier.label)}`}
                      size="mini"
                      iconRight={{ icon: "link", fill: true }}
                      fullWidth
                      addClass={Styles.plusBtn}
                    />
                  </Link>
                )}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}